import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { formatDate } from '../../lib/utils';
import { RescueGwaPostcardOrderGql, RescueGwaPostcardOrderStatus } from '../../lib/rescueQueries';
import PortalTable, { Column } from '../../components/PortalTable/PortalTable';
import StatusBadge, { BadgeVariant } from '../../components/StatusBadge/StatusBadge';
import TrackingCell from '../../components/TrackingCell/TrackingCell';
import Button from '../../components/Button/Button';
import { type AdoptionCardPreviews, downloadAdoptionCardSample } from './useAdoptionCardPreviews';
import styles from './GwaPostcards.module.scss';

// queued/submitted are internal print-pipeline steps between approval and production; rescues
// just see them as "Approved".
const STATUS_LABELS: Record<RescueGwaPostcardOrderStatus, string> = {
  pending_approval: 'Pending Approval',
  queued: 'Approved',
  submitted: 'Approved',
  in_production: 'In Production',
  shipped: 'In Transit',
  delivered: 'Delivered',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_VARIANTS: Record<RescueGwaPostcardOrderStatus, BadgeVariant> = {
  pending_approval: 'warning',
  queued: 'success',
  submitted: 'success',
  in_production: 'info',
  shipped: 'info',
  delivered: 'success',
  failed: 'error',
  cancelled: 'neutral',
};

const orderColumns: Column[] = [
  { key: 'orderNumber', label: 'Order #' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'orderDate', label: 'Ordered' },
  { key: 'status', label: 'Status' },
  { key: 'tracking', label: 'Tracking' },
];

function shortOrderId(id: string) {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CardLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className={styles.lightbox} onClick={onClose} role="presentation">
      <button type="button" className={styles.lightboxClose} aria-label="Close preview">
        &times;
      </button>
      <img src={src} alt={alt} />
    </div>,
    document.body,
  );
}

function CardThumb({ src, caption, onExpand }: { src: string; caption: string; onExpand: () => void }) {
  return (
    <button
      type="button"
      className={styles.cardThumb}
      onClick={onExpand}
      aria-label={`Enlarge the card ${caption.toLowerCase()}`}
    >
      <span className={styles.thumbZoom} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M15 3h6v6M14 10l7-7M9 21H3v-6M10 14l-7 7" />
        </svg>
      </span>
      <img src={src} alt={`Adoption card ${caption.toLowerCase()}`} />
      <span className={styles.thumbCap}>{caption}</span>
    </button>
  );
}

function CardPreviews({
  previews,
  onExpand,
}: {
  previews: AdoptionCardPreviews;
  onExpand: (src: string, caption: string) => void;
}) {
  if (previews.error) {
    return (
      <div className={styles.previewPlaceholder}>
        {previews.pdfUrl ? (
          <span>
            Preview images unavailable —{' '}
            <a href={previews.pdfUrl} target="_blank" rel="noreferrer">
              open the PDF preview
            </a>
            .
          </span>
        ) : (
          'Preview unavailable right now — please try refreshing.'
        )}
      </div>
    );
  }
  const { front, back } = previews;
  if (previews.loading || !front) {
    return (
      <div className={styles.cardsPreviewRow} aria-label="Generating card preview" aria-busy="true">
        <div className={styles.thumbLoading} />
        <div className={styles.thumbLoading} />
      </div>
    );
  }
  return (
    <div className={styles.cardsPreviewRow}>
      <CardThumb src={front} caption="Front" onExpand={() => onExpand(front, 'Front')} />
      {back && <CardThumb src={back} caption="Back" onExpand={() => onExpand(back, 'Back')} />}
    </div>
  );
}

function badgeFor(status: RescueGwaPostcardOrderStatus) {
  return <StatusBadge variant={STATUS_VARIANTS[status] ?? 'neutral'}>{STATUS_LABELS[status] ?? status}</StatusBadge>;
}

function CardOrdersTable({ orders }: { orders: RescueGwaPostcardOrderGql[] }) {
  return (
    <PortalTable columns={orderColumns} isEmpty={orders.length === 0} emptyMessage="No card orders yet">
      {orders.map((order) => (
        <tr key={order.id}>
          <td title={order.id}>{shortOrderId(order.id)}</td>
          <td>{order.quantity} cards</td>
          <td>{formatDate(order.createdAt)}</td>
          <td>{badgeFor(order.status)}</td>
          <td>
            <TrackingCell trackingNumber={order.trackingNumber} />
          </td>
        </tr>
      ))}
    </PortalTable>
  );
}

interface GwaPostcardsSectionProps {
  orders: RescueGwaPostcardOrderGql[];
  previews: AdoptionCardPreviews;
  /** False for a rescue with past orders that is no longer in the cards beta: history, no CTA. */
  canOrderCards: boolean;
  onOrderCards: () => void;
}

export default function GwaPostcardsSection({
  orders,
  previews,
  canOrderCards,
  onOrderCards,
}: GwaPostcardsSectionProps) {
  const [lightbox, setLightbox] = useState<{ src: string; caption: string } | null>(null);
  const [downloading, setDownloading] = useState(false);

  const onDownload = async () => {
    if (!previews.pdfUrl) {
      return;
    }
    setDownloading(true);
    try {
      await downloadAdoptionCardSample(previews.pdfUrl);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={styles.postcardsCard}>
      <div className={styles.cardTitle}>Gift With Adoption Cards</div>
      <div className={styles.cardSubtitle}>for your adoption packet</div>

      <div className={styles.cardsFlex}>
        <CardPreviews previews={previews} onExpand={(src, caption) => setLightbox({ src, caption })} />
        <div className={styles.cardsCopy}>
          <p className={styles.cardDescription}>
            Printed cards, co-branded with your rescue, ready to drop into every adoption packet. Each card carries your
            QR code and promo code so adopters can claim their free Fi Mini on the way home.
          </p>
          <ul className={styles.benefitsList}>
            <li>
              <CheckIcon />
              Personalized with your rescue name and unique link
            </li>
            <li>
              <CheckIcon />
              Order 100–500 cards in packs of 100 — free to your rescue
            </li>
            <li>
              <CheckIcon />
              Reviewed and approved like your device orders
            </li>
          </ul>
          <div className={styles.cardsActions}>
            {canOrderCards && (
              <Button variant="green" onClick={onOrderCards}>
                Order cards
              </Button>
            )}
            <Button variant="greenOutline" onClick={onDownload} disabled={!previews.pdfUrl || downloading}>
              Download
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.ordersHeading}>Your card orders</div>
      <CardOrdersTable orders={orders} />

      {lightbox && (
        <CardLightbox
          src={lightbox.src}
          alt={`Adoption card ${lightbox.caption.toLowerCase()}`}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
